import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';
import { Star, Clock, Users, MapPin, CheckCircle } from 'lucide-react';

const seaActivities = [
  {
    id: 6,
    title: "Croisière Musandam",
    location: "Oman",
    price: "145.20",
    originalPrice: "180.00",
    duration: "Journée complète",
    groupSize: "Groupe",
    rating: 4.8,
    reviews: 1567,
    image: "https://mesvacancesadubai.com/wp-content/uploads/2020/12/musandam-cruise-.jpg.webp",
    badge: "Nature",
    highlights: ["Fjords d'Arabie", "Déjeuner à bord", "Snorkeling", "Dauphins"],
    description: "Explorez les fjords spectaculaires d'Oman lors d'une croisière inoubliable. Observation des dauphins, snorkeling dans les eaux cristallines, et déjeuner à bord."
  },
  {
    id: 11,
    title: "Dîner Croisière Dhow",
    location: "Dubai Creek",
    price: "68.90",
    originalPrice: "85.00",
    duration: "2 heures",
    groupSize: "Groupe",
    rating: 4.7,
    reviews: 1892,
    image: "https://mesvacancesadubai.com/wp-content/uploads/2020/12/musandam-cruise-.jpg.webp",
    badge: "Culture",
    highlights: ["Dîner buffet", "Spectacle traditionnel", "Vue sur la ville", "Boissons incluses"],
    description: "Dînez à bord d'un dhow traditionnel sur Dubai Creek. Buffet international, spectacles de danse, et vue panoramique sur la skyline de Dubaï."
  },
  {
    id: 12,
    title: "Observation Baleines & Dauphins",
    location: "Golfe Persique",
    price: "95.50",
    originalPrice: "120.00",
    duration: "4 heures",
    groupSize: "Petit groupe",
    rating: 4.8,
    reviews: 567,
    image: "https://mesvacancesadubai.com/wp-content/uploads/2020/12/aquaventure-.jpg.webp",
    badge: "Nature",
    highlights: ["Observation garantie", "Guide naturaliste", "Snack à bord", "Photos offertes"],
    description: "Partez à la rencontre des baleines et dauphins dans le Golfe Persique. Excursion guidée par un naturaliste avec garantie d'observation."
  },
  {
    id: 5,
    title: "Jetski & Flyboard",
    location: "Dubai Marina",
    price: "84.70",
    originalPrice: "110.00",
    duration: "2 heures",
    groupSize: "1-2 personnes",
    rating: 4.9,
    reviews: 892,
    image: "https://mesvacancesadubai.com/wp-content/uploads/2020/11/jetski-3.jpg",
    badge: "Aventure",
    highlights: ["Jetski 30min", "Flyboard 15min", "Équipement inclus", "Instructeur français"],
    description: "Sensations fortes garanties avec jetski et flyboard dans les eaux de Dubai Marina. Équipement professionnel et instructeur francophone."
  }
];

export default function SeaActivitiesPage() {
  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-6 pb-8 bg-gradient-to-r from-blue-600 to-cyan-600 text-white relative">
        <div className="absolute inset-0 bg-[url('https://mesvacancesadubai.com/wp-content/uploads/2020/12/musandam-cruise-.jpg.webp')] bg-cover bg-center opacity-30"></div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <h1 className="text-4xl lg:text-5xl font-bold mb-2">Activités en Mer</h1>
          <p className="text-lg text-blue-50 max-w-2xl">Découvrez les eaux turquoise du Golfe Persique</p>
        </div>
      </section>

      {/* Activities */}
      <section className="py-12 flex-grow">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {seaActivities.map((activity) => (
              <div key={activity.id} className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group">
                <div className="relative overflow-hidden">
                  <img
                    src={activity.image}
                    alt={activity.title}
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {activity.badge}
                    </span>
                  </div>
                  <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-2">
                    <div className="text-right">
                      <div className="text-lg font-bold text-blue-600">€{activity.price}</div>
                      <div className="text-sm text-gray-500 line-through">€{activity.originalPrice}</div>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-1">
                      <Star className="text-amber-400 fill-current" size={16} />
                      <span className="font-semibold text-gray-900">{activity.rating}</span>
                      <span className="text-gray-500 text-sm">({activity.reviews} avis)</span>
                    </div>
                    <div className="flex items-center text-gray-500 text-sm">
                      <MapPin size={14} className="mr-1" />
                      {activity.location}
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                    {activity.title}
                  </h3>

                  <p className="text-gray-600 text-sm mb-4">{activity.description}</p>

                  <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                    <div className="flex items-center">
                      <Clock size={14} className="mr-1" />
                      {activity.duration}
                    </div>
                    <div className="flex items-center">
                      <Users size={14} className="mr-1" />
                      {activity.groupSize}
                    </div>
                  </div>

                  <div className="mb-6">
                    <div className="flex flex-wrap gap-1">
                      {activity.highlights.map((highlight, index) => (
                        <span key={index} className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                          <CheckCircle size={12} className="inline mr-1" />
                          {highlight}
                        </span>
                      ))}
                    </div>
                  </div>

                  <Button className="w-full text-sm py-2">
                    Réserver maintenant
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

